import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";
import { PN_TYPE_LABELS } from "@/types/design";

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  designProjectId: string;
  pns?: any[];
  revisions?: any[];
  onSaved?: () => void;
}

export function DesignRevisionDialog({ open, onOpenChange, designProjectId, pns = [], revisions = [], onSaved }: Props) {
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({ design_part_number_id: "", revision_number: 1, revision_date: "", changes: "", responsible_id: "", status: "draft" });

  const { data: profiles } = useQuery({
    queryKey: ["profiles-rev-design"],
    queryFn: async () => {
      const { data } = await api.from("profiles").select("id,full_name").eq("is_active", true).order("full_name");
      return data || [];
    },
  });

  const nextNumberFor = (pnId: string) =>
    (revisions || []).filter((r: any) => r.design_part_number_id === pnId)
      .reduce((m: number, r: any) => Math.max(m, r.revision_number || 0), 0) + 1;

  useEffect(() => {
    if (!open) return;
    const onlyPn = pns.length === 1 ? pns[0].id : "";
    setF({ design_part_number_id: onlyPn, revision_number: onlyPn ? nextNumberFor(onlyPn) : 1, revision_date: new Date().toISOString().slice(0, 10), changes: "", responsible_id: "", status: "draft" });
  }, [open]);

  const onPickPn = (v: string) => {
    const pnId = v === "none" ? "" : v;
    setF((p) => ({ ...p, design_part_number_id: pnId, revision_number: pnId ? nextNumberFor(pnId) : 1 }));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!f.design_part_number_id) { toast.error("Selecciona el PN al que pertenece esta revisión"); return; }
    setLoading(true);
    try {
      const { error } = await api.from("design_revisions").insert([{
        design_project_id: designProjectId,
        design_part_number_id: f.design_part_number_id,
        revision_number: f.revision_number,
        revision_date: f.revision_date || null,
        changes: f.changes || null,
        responsible_id: f.responsible_id || null,
        status: f.status,
      }]);
      if (error) throw error;
      toast.success(`Revisión ${f.revision_number} añadida`);
      onOpenChange(false);
      onSaved?.();
    } catch (err: any) {
      toast.error(err.message || "No se pudo guardar la revisión");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Nueva Revisión</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Part Number (PN) *</Label>
            <Select value={f.design_part_number_id || "none"} onValueChange={onPickPn}>
              <SelectTrigger><SelectValue placeholder="Selecciona un PN" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Selecciona un PN</SelectItem>
                {pns.map((p: any) => <SelectItem key={p.id} value={p.id}>{p.pn_code} · {PN_TYPE_LABELS[p.pn_type]}</SelectItem>)}
              </SelectContent>
            </Select>
            {pns.length === 0 && <p className="text-[11px] text-amber-600 mt-1">Genera primero un PN para poder añadir revisiones.</p>}
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>Nº Revisión</Label>
              <Input type="number" value={f.revision_number} onChange={(e) => setF({ ...f, revision_number: parseInt(e.target.value) || 1 })} min={1} />
            </div>
            <div className="col-span-2">
              <Label>Fecha</Label>
              <Input type="date" value={f.revision_date} onChange={(e) => setF({ ...f, revision_date: e.target.value })} />
            </div>
          </div>
          <div>
            <Label>Cambios aprobados</Label>
            <Textarea value={f.changes} onChange={(e) => setF({ ...f, changes: e.target.value })} rows={4} placeholder="Describe los cambios de esta revisión..." />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Responsable</Label>
              <Select value={f.responsible_id || "none"} onValueChange={(v) => setF({ ...f, responsible_id: v === "none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="Sin asignar" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin asignar</SelectItem>
                  {profiles?.map((p: any) => <SelectItem key={p.id} value={p.id}>{p.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Estado</Label>
              <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Borrador</SelectItem>
                  <SelectItem value="approved">Aprobada</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={loading || pns.length === 0}>{loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Añadir</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
